import Chip from '@components/v2/ui/Chip/Chip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { Fragment } from 'react';
import { BusinessPolicyAstLogicalOperator } from '../../../../../../../services/modules/business-policies/businessPolicyCondition.types';
import { OrderConditionsTestIds } from '../../../../../../../utils/testIds/OrderConditions.testIds';
import { businessPolicyTypeTranslator } from '../../../../../../business-policies/business-policy.options';
import BusinessPolicyCriteriaConnector from '../../../../../../business-policies/form/criteria-and-rewards-form/criteria/components/BusinessPolicyCriteriaConnector';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { BusinessPolicyViolationView } from '../businessPolicyViolation.types';
import BusinessPolicyViolationCriterionRow from './BusinessPolicyViolationCriterionRow';
import BusinessPolicyViolationTitle from './BusinessPolicyViolationTitle';

interface BusinessPolicyViolationDetailsProps {
  violations: BusinessPolicyViolationView[];
}

const BusinessPolicyViolationDetails = ({ violations }: BusinessPolicyViolationDetailsProps) => {
  const { translate } = useTranslation();

  return (
    <Stack sx={{ gap: 3 }}>
      {violations.map((violation) => (
        <Stack
          key={`${violation.businessPolicy.id}-${violation.messageCode}`}
          test-id={OrderConditionsTestIds.POLICY_VIOLATION}
          sx={{ gap: 1.5 }}
        >
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Chip
              size="extraSmall"
              color="secondary"
              label={translate(businessPolicyTypeTranslator[violation.businessPolicy.type])}
            />
            <Typography
              variant="subtitle2"
              noWrap
              title={translate(violation.messageCode, { expected: violation.businessPolicy.name })}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <BusinessPolicyViolationTitle
                messageCode={violation.messageCode}
                policyName={violation.businessPolicy.name}
              />
            </Typography>
          </Stack>

          {violation.branches.map((branch, branchIndex) => (
            <Fragment key={branch.criteria[0]?.nodeId ?? branchIndex}>
              {branchIndex > 0 && (
                <BusinessPolicyCriteriaConnector operator={BusinessPolicyAstLogicalOperator.OR} />
              )}

              <Stack
                sx={(theme) => ({
                  gap: 1.5,
                  padding: 1.5,
                  borderRadius: `${theme.shape.borderRadiusXl}px`,
                  border: `1px solid ${
                    branch.isViolated
                      ? theme.palette.error.main
                      : theme.palette.custom.border.primary
                  }`,
                })}
              >
                {branch.criteria.map((criterion, criterionIndex) => (
                  <Fragment key={criterion.nodeId || criterionIndex}>
                    {criterionIndex > 0 && branch.criteriaOperator && (
                      <BusinessPolicyCriteriaConnector operator={branch.criteriaOperator} />
                    )}
                    <BusinessPolicyViolationCriterionRow criterion={criterion} />
                  </Fragment>
                ))}
              </Stack>
            </Fragment>
          ))}
        </Stack>
      ))}
    </Stack>
  );
};

export default BusinessPolicyViolationDetails;
